import { isBoolean } from 'lodash';
import { SetOptional } from 'type-fest';
import { BuyProductsMutation } from '../graphql/types';
import { getAttributionData } from './attribution';
import { getGrowthBookDeviceId } from './growthbook';
import { setGa4UserId } from './ga4User';
import { sendGoogleAdsSignup } from './googleAdsLead';
import { isStaffAnalyticsSession } from './staffAnalytics';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rudderanalytics: any;
  }
}

// src/html/analytics.*.html configure GA4 in this group. Events without send_to also reach the
// Google Ads tag, which RudderStack configures in gtag's default group.
const GA4_GROUP = 'ga4';

function sendGa4Event(name: string, params: Record<string, unknown>) {
  window.gtag?.('event', name, { ...params, send_to: GA4_GROUP });
}

/**
 * Returns experiment attribution data to be included in conversion events.
 * Includes gb_device_id (for experiment attribution).
 */
function getExperimentAttribution() {
  const deviceId = getGrowthBookDeviceId();
  return {
    ...(deviceId ? { gb_device_id: deviceId } : {}),
  };
}

function getConversionAttribution() {
  return {
    ...getAttributionData(),
    ...getExperimentAttribution(),
  };
}

/**
 * Alias user for Vero profile merging.
 * Used when a user already signed up for the mailing list before signing up for the app.
 */
export const aliasUser = (userId: string, email: string) => {
  if (isStaffAnalyticsSession()) return;
  window.rudderanalytics?.alias?.(userId, email);
};

interface IdentifyUserTraits {
  email: string;
  firstName?: string;
  lastName?: string;
  stripe_customer_id?: string;
  attribution_source?: string;
  attribution_medium?: string;
  attribution_campaign?: string;
  attribution_id?: string;
  attribution_term?: string;
  attribution_content?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  first_touch_channel?: string;
  first_landing_page?: string;
  first_referrer?: string;
  first_seen_at?: string;
  marketing_anonymous_id?: string;
}

interface IdentifyUserOptions {
  intercomUserHash?: string;
}

/**
 * Identify a user across all analytics platforms.
 */
export const identifyUser = (userId: string, traits: IdentifyUserTraits, options?: IdentifyUserOptions) => {
  if (isStaffAnalyticsSession()) return;
  setGa4UserId(userId);
  const integrationOptions = options?.intercomUserHash
    ? { integrations: { Intercom: { user_hash: options.intercomUserHash } } }
    : undefined;

  window.rudderanalytics?.identify?.(userId, traits, integrationOptions);
};

type CheckoutSourceEnum = 'new_purchase_page' | 'stripe_checkout';

interface TrackOrderCompletedProps {
  userId: number;
  invoiceId: string;
  amountPaid: number;
  couponCode?: string | null;
  couponPercentOff?: number | null;
  analyticsProducts: Array<
    SetOptional<BuyProductsMutation['buyProducts']['analyticsProducts'][0], '__typename' | 'is_upgrade'>
  >;
  checkoutSource?: CheckoutSourceEnum;
  checkoutSessionId?: string;
}

export const trackOrderCompleted = ({
  userId,
  invoiceId,
  amountPaid,
  couponCode = null,
  couponPercentOff = null,
  analyticsProducts,
  checkoutSource = 'new_purchase_page',
  checkoutSessionId,
}: TrackOrderCompletedProps) => {
  if (isStaffAnalyticsSession()) return;
  const isUpgrade = analyticsProducts.some(({ is_upgrade }) => !!is_upgrade);

  const total = amountPaid / 100;
  const coupon = couponCode && couponPercentOff && `${couponCode} - ${couponPercentOff}%`;

  // Google Analytics 4 (GA4) purchase event
  sendGa4Event('purchase', {
    currency: 'USD',
    transaction_id: invoiceId,
    value: total,
    coupon: coupon,
    items: analyticsProducts.map(({ name, product_id, price, quantity }) => ({
      item_id: product_id,
      item_name: name,
      price: price / 100,
      quantity,
    })),
    ...getConversionAttribution(),
  });

  // RudderStack purchase event
  window.rudderanalytics?.track?.('Order Completed', {
    order_id: invoiceId,
    event_id: `purchase_${invoiceId}`,
    ...(checkoutSessionId ? { checkout_session_id: checkoutSessionId } : {}),
    total: total,
    revenue: total,
    coupon: coupon,
    products: analyticsProducts.map(({ name, product_id, is_upgrade, price, quantity }) => ({
      name,
      product_id,
      price: price / 100,
      quantity,
      ...(isBoolean(is_upgrade) ? { is_upgrade } : {}),
    })),
    token_tax_id: userId,
    currency: 'USD',
    is_upgrade: isUpgrade,
    status: 'completed',
    checkout_source: checkoutSource,
    ...getConversionAttribution(),
  });
};

interface AccountDetailsArgs {
  hijacked_account_email: string;
  hijacked_account_id: number;
  is_hijacked: boolean;
}

interface TrackTxnDeleteArgs extends AccountDetailsArgs {
  txn_delete_quantity: number;
  txn_ids: string;
}
export const trackTxnDelete = (args: TrackTxnDeleteArgs) => {
  window.rudderanalytics?.track?.('Transaction Deleted', { ...args });
};

interface TrackBatchTxnDeleteArgs extends AccountDetailsArgs {
  filter_query: string;
}
export const trackBatchTxnDelete = (args: TrackBatchTxnDeleteArgs) => {
  window.rudderanalytics?.track?.('Transaction Batch Deleted', { ...args });
};

interface TrackIntegrationAddedArgs extends AccountDetailsArgs {
  type: 'CSV' | 'API' | 'OAUTH';
  name?: string;
}
export const trackIntegrationAdded = (args: TrackIntegrationAddedArgs) => {
  window.rudderanalytics?.track?.('Integration Added', { ...args });
};

interface TrackIntegrationEditedArgs extends AccountDetailsArgs {
  credential_type: string;
}
export const trackIntegrationEdited = (args: TrackIntegrationEditedArgs) => {
  window.rudderanalytics?.track?.('Integration Edited', { ...args });
};

export const trackIntegrationDeleted = (args: TrackIntegrationEditedArgs) => {
  window.rudderanalytics?.track?.('Integration Deleted', { ...args });
};

export const trackTaxDashboardRecalculated = (args: AccountDetailsArgs) => {
  window.rudderanalytics?.track?.('TaxDashboard Recalculated', { ...args });
};

interface TrackIntegrationResyncedArgs extends AccountDetailsArgs {
  hard_resync: boolean;
  credential_ids: string;
}

export const trackIntegrationResynced = (args: TrackIntegrationResyncedArgs) => {
  window.rudderanalytics?.track?.('Integration Resynced', { ...args });
};

interface TrackTaxYearLocking extends AccountDetailsArgs {
  year: number;
  is_admin: boolean;
}

export const trackTaxYearLocked = (args: TrackTaxYearLocking) => {
  window.rudderanalytics?.track?.('TaxYear Locked', { ...args });
};

export const trackTaxYearUnlocked = (args: TrackTaxYearLocking) => {
  window.rudderanalytics?.track?.('TaxYear Unlocked', { ...args });
};

export interface TrackVipUpsellBannerArgs {
  experimentVersion: string;
  experimentGroup: string;
  url?: string;
}

export const trackVipUpsellBannerLoaded = (args: TrackVipUpsellBannerArgs) => {
  window.rudderanalytics?.track?.('VipUpsellBanner Loaded', { ...args });
};

export const trackVipUpsellBannerDismissed = (args: TrackVipUpsellBannerArgs) => {
  window.rudderanalytics?.track?.('VipUpsellBanner Dismissed', { ...args });
};

export const trackVipUpsellBannerClicked = (args: TrackVipUpsellBannerArgs) => {
  window.rudderanalytics?.track?.('VipUpsellBanner Clicked', { ...args });
};

interface TrackConsultFormStepArgs {
  step: number;
  stepName: string;
  property: string;
  source: string;
}

export const trackConsultFormStepCompleted = (args: TrackConsultFormStepArgs) => {
  window.rudderanalytics?.track?.('Consult Form Step Completed', { ...args });
};

interface TrackConsultFormSubmittedArgs {
  property: string;
  source: string;
}

export const trackConsultFormSubmitted = (args: TrackConsultFormSubmittedArgs) => {
  sendGa4Event('consult_form_submitted', { ...args });

  window.rudderanalytics?.track?.('Consult Form Submitted', { ...args });
};

interface CartItem {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
}

interface TrackAddToCartArgs {
  value: number;
  currency?: string;
  items: CartItem[];
}

export const trackAddToCart = ({ value, currency = 'USD', items }: TrackAddToCartArgs) => {
  // Google Analytics 4 (GA4) add_to_cart event
  sendGa4Event('add_to_cart', {
    currency,
    value,
    items,
    property: 'app',
    ...getConversionAttribution(),
  });

  window.rudderanalytics?.track?.('Product Added', {
    product_id: items[0]?.item_id,
    name: items[0]?.item_name,
    price: items[0]?.price,
    quantity: items[0]?.quantity,
    currency,
    value,
    property: 'app',
    ...getConversionAttribution(),
  });
};

interface TrackRemoveFromCartArgs {
  value: number;
  currency?: string;
  items: CartItem[];
}

export const trackRemoveFromCart = ({ value, currency = 'USD', items }: TrackRemoveFromCartArgs) => {
  // Google Analytics 4 (GA4) remove_from_cart event
  sendGa4Event('remove_from_cart', {
    currency,
    value,
    items,
    property: 'app',
    ...getConversionAttribution(),
  });

  window.rudderanalytics?.track?.('Product Removed', {
    cart_id: 'purchase_cart',
    product_id: items[0]?.item_id,
    name: items[0]?.item_name,
    price: items[0]?.price,
    quantity: items[0]?.quantity,
    currency,
    value,
    property: 'app',
    ...getConversionAttribution(),
  });
};

interface TrackBeginCheckoutArgs {
  value: number;
  currency?: string;
  coupon?: string;
  items: CartItem[];
}

export const trackBeginCheckout = ({ value, currency = 'USD', coupon, items }: TrackBeginCheckoutArgs) => {
  // Google Analytics 4 (GA4) begin_checkout event
  sendGa4Event('begin_checkout', {
    currency,
    value,
    ...(coupon ? { coupon } : {}),
    items,
    property: 'app',
    ...getConversionAttribution(),
  });

  window.rudderanalytics?.track?.('Checkout Started', {
    value,
    revenue: value,
    currency,
    coupon,
    products: items,
    property: 'app',
    ...getConversionAttribution(),
  });
};

// Sign up tracking
interface TrackSignUpCompletedArgs {
  location: string;
  type?: string;
  country?: string;
  onboarding_bucket?: string;
  checkout_source?: string;
}

const GA4_SIGN_UP_PREFIX = 'tt_ga4_sign_up:';

function claimGa4SignUp(email: string): boolean {
  const storageKey = `${GA4_SIGN_UP_PREFIX}${email.trim().toLowerCase()}`;
  try {
    if (sessionStorage.getItem(storageKey) === '1') return false;
    sessionStorage.setItem(storageKey, '1');
    return true;
  } catch {
    return true;
  }
}

export const trackSignUpCompleted = (args: TrackSignUpCompletedArgs, email: string) => {
  window.rudderanalytics?.track?.('sign_up_completed', { ...args, ...getConversionAttribution() });
  sendGoogleAdsSignup(email);
  if (isStaffAnalyticsSession() || !claimGa4SignUp(email)) return;
  sendGa4Event('sign_up', { method: args.type ?? 'email' });
};

export const trackSignUpStarted = (location: string) => {
  window.rudderanalytics?.track?.('sign_up_started', { location, ...getConversionAttribution() });
};

// Password reset tracking
export const trackForgotPasswordSubmit = (location: string) => {
  window.rudderanalytics?.track?.('forgot_password_submit', { location });
};

export const trackForgotPasswordFailed = (location: string) => {
  window.rudderanalytics?.track?.('forgot_password_failed', { location });
};

export const trackResetPasswordViewed = (location: string) => {
  window.rudderanalytics?.track?.('Reset Password', { location });
};

export const trackPasswordResetCompleted = (location: string) => {
  window.rudderanalytics?.track?.('password_reset_completed', { location });
};

export const trackPasswordResetFailed = (location: string) => {
  window.rudderanalytics?.track?.('password_reset_failed', { location });
};

// CSV tracking
export const trackCsvDuplicateUploadAttempted = (location: string) => {
  window.rudderanalytics?.track?.('csv_duplicate_upload_attempted', { location });
};

export const trackTransactionCsvExported = () => {
  window.rudderanalytics?.track?.('transaction_csv_exported');
};

// VIP tracking
export const trackCompletedVipVideo = () => {
  window.rudderanalytics?.track?.('completed_vip_video');
};

// Self Recon tracking
interface TrackSelfReconOverviewArgs {
  missingWalletCount: number;
  missingExchangeCount: number;
  unreconciledTxnsCount?: number;
}

export const trackSelfReconOverview = (args: TrackSelfReconOverviewArgs) => {
  window.rudderanalytics?.track?.('Self Recon Overview', args);
};

interface TrackUnreconciledTxArgs {
  txHash: string;
  chain: string;
  import: number;
  suggestedTreatment: number | null;
}

export const trackUnreconciledTransactionViewed = (args: TrackUnreconciledTxArgs) => {
  window.rudderanalytics?.track?.('UnreconciledTx', args);
};

export const trackTokensLoadingTime = (milliseconds: number) => {
  const duration = milliseconds < 10 ? 0 : milliseconds;
  window.rudderanalytics?.track?.('UnreconciledTx TokensLoaded (ms)', { duration });
};

export const trackUnreconciledTxTypeSelected = (type: string) => {
  window.rudderanalytics?.track?.('UnreconciledTx TypeSelected', { type });
};

export const trackUnreconciledTxReconciled = (type: string) => {
  window.rudderanalytics?.track?.('UnreconciledTx Reconciled', { typeSaved: type });
};

export const trackUnreconciledTxSkipped = () => {
  window.rudderanalytics?.track?.('UnreconciledTx Skipped');
};

interface TrackUnreconciledDraftTypeEditedArgs {
  originalType: string | null;
  updatedType: string;
}

export const trackUnreconciledDraftTypeEdited = (args: TrackUnreconciledDraftTypeEditedArgs) => {
  window.rudderanalytics?.track?.('UnreconciledDraft TypeEdited', args);
};

interface TrackUnreconciledDraftTransferEditedArgs {
  symbolEdited: boolean;
  quantityEdited: boolean;
}

export const trackUnreconciledDraftTransferEdited = (args: TrackUnreconciledDraftTransferEditedArgs) => {
  window.rudderanalytics?.track?.('UnreconciledDraft TransferEdited', args);
};
